import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import I3PageHero from '../components/I3PageHero';
import I3MediaViewer from '../components/I3MediaViewer';
import I3EventsHeroCarousel from '../components/I3EventsHeroCarousel';
import I3EventsPhotoRotator from '../components/I3EventsPhotoRotator';
import { useRevealSections } from '../hooks/useReveal';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import I3RichHtml from '../components/I3RichHtml';
import { resolvePublicMediaSrc } from '../utils/i3Assets';
import '../styles/I3Page.css';
import '../styles/I3Reportage.css';
import '../styles/I3Events.css';

type ViewerState = { open: boolean; type: 'video' | 'image'; src: string; title?: string };

const I3Events: React.FC = () => {
  const { content, loading, error, ready } = useI3SiteContent();
  const ep = content.eventsPage;
  const lh = ep.latestHighlight;
  const [viewer, setViewer] = useState<ViewerState>({ open: false, type: 'image', src: '' });
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  const openViewer = (type: 'video' | 'image', src: string, title?: string) => {
    const resolved = resolvePublicMediaSrc(src);
    if (!resolved) return;
    setViewer({ open: true, type, src: resolved, title });
  };
  const closeViewer = () => setViewer((v) => ({ ...v, open: false }));

  const heroSubtitle = ep.pageSubtitle ?? 'Join us for workshops and retreats';

  const videos = lh.videos.slice(0, 4);

  return (
    <article className="i3-page i3-reportage-page i3-events-page">
      <I3PageHero showLogo title="Events" subtitle={heroSubtitle} />
      <div className="i3-page-wrap" ref={wrapRef}>
        {loading && !ready && <p className="i3-cms-loading">Loading events from CMS…</p>}
        {error && ready && (
          <p className="i3-cms-error" role="status">
            Using offline content (CMS fetch failed). {error.message}
          </p>
        )}

        <section className="i3-section i3-events-highlight i3-reveal" aria-labelledby="i3-events-highlight-title">
          <div className="i3-events-highlight-grid">
            <div className="i3-events-highlight-copy">
              {lh.label ? <span className="i3-block-label">{lh.label}</span> : null}
              <h2 id="i3-events-highlight-title" className="i3-events-highlight-title">
                {lh.title}
              </h2>
              {lh.dateLine ? <p className="i3-events-highlight-dateline">{lh.dateLine}</p> : null}
              <I3RichHtml className="i3-events-highlight-body" html={lh.descriptionHtml} />
              {lh.blogPostSlug ? (
                <p className="i3-events-highlight-blog">
                  <Link to={`/blog/${lh.blogPostSlug}`} className="i3-cta i3-cta-secondary">
                    Read the story on the Journal
                  </Link>
                </p>
              ) : null}
            </div>
            <I3EventsHeroCarousel slides={lh.carouselSlides} onOpen={openViewer} className="i3-events-highlight-carousel" />
          </div>
        </section>

        {videos.length > 0 ? (
          <section className="i3-section i3-events-video-strip i3-reveal" aria-label="Video highlights">
            <h3 className="i3-events-strip-title">Video highlights</h3>
            <div className="i3-events-video-grid">
              {videos.map((m, i) => {
                const vSrc = resolvePublicMediaSrc(m.src);
                return (
                <button
                  key={`${m.src}-${i}`}
                  type="button"
                  className="i3-events-video-tile"
                  onClick={() => openViewer('video', m.src, m.title)}
                  aria-label={m.title ? `Play video: ${m.title}` : 'Play video'}
                >
                  {vSrc ? (
                    <video
                      className="i3-events-video-tile-bg"
                      src={vSrc}
                      muted
                      playsInline
                      preload="metadata"
                      aria-hidden
                    />
                  ) : null}
                  <span className="i3-events-video-tile-play" aria-hidden>
                    ▶
                  </span>
                  {m.title ? <span className="i3-events-video-tile-label">{m.title}</span> : null}
                </button>
              );
              })}
            </div>
            <p className="i3-events-strip-hint">Tap play to open fullscreen. Use the close control to return.</p>
          </section>
        ) : null}

        {lh.photos.length > 0 ? (
          <section className="i3-section i3-events-photo-section i3-reveal" aria-label="Photo highlights">
            <h3 className="i3-events-strip-title">Moments in photos</h3>
            <I3EventsPhotoRotator
              photos={lh.photos}
              onOpen={(src, title) => openViewer('image', src, title)}
            />
            <p className="i3-events-strip-hint">Click the image for a fullscreen view.</p>
          </section>
        ) : null}

        <section className="i3-section i3-events-upcoming i3-reveal" aria-labelledby="i3-events-upcoming-title">
          <h2 id="i3-events-upcoming-title" className="i3-events-upcoming-head">
            {ep.upcomingSectionTitle ?? 'Up next'}
          </h2>
          {ep.upcomingEvents.length > 0 ? (
            <ul className="i3-events-upcoming-list">
              {ep.upcomingEvents.map((ue) => (
                <li key={ue.id} className="i3-events-upcoming-card">
                  <span className="i3-events-upcoming-label">Upcoming</span>
                  <h3 className="i3-events-upcoming-title">{ue.title}</h3>
                  {ue.date ? (
                    <p className="i3-events-upcoming-line">
                      <strong>Date</strong> {ue.date}
                    </p>
                  ) : null}
                  {ue.time ? (
                    <p className="i3-events-upcoming-line">
                      <strong>Time</strong> {ue.time}
                    </p>
                  ) : null}
                  {ue.checkIn ? (
                    <p className="i3-events-upcoming-line i3-events-upcoming-checkin">{ue.checkIn}</p>
                  ) : null}
                  {ue.location ? (
                    <p className="i3-events-upcoming-line">
                      <strong>Location</strong> {ue.location}
                    </p>
                  ) : null}
                  {ue.descriptionHtml ? (
                    <I3RichHtml className="i3-events-upcoming-desc" html={ue.descriptionHtml} />
                  ) : null}
                  <div className="i3-events-upcoming-actions">
                    {ue.registerUrl ? (
                      <a
                        href={ue.registerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="i3-cta i3-cta-primary"
                      >
                        {ue.ctaLabel ?? 'Register'}
                      </a>
                    ) : (
                      <Link to="/contact" className="i3-cta i3-cta-primary">
                        {ue.ctaLabel ?? 'Contact us to save a spot'}
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="i3-events-upcoming-empty">
              {ep.upcomingEmptyHtml ? <I3RichHtml html={ep.upcomingEmptyHtml} /> : null}
              <p className="i3-events-upcoming-fallback">
                <Link to="/contact">Contact us</Link> to hear about the next workshop or retreat.
                Stories and reflections live on the{' '}
                <Link to="/blog">Journal</Link>.
              </p>
            </div>
          )}
          {ep.listingWorkflowNoteHtml ? (
            <I3RichHtml className="i3-events-workflow-note-wrap" html={ep.listingWorkflowNoteHtml} />
          ) : null}
        </section>

        <section className="i3-section i3-events-more i3-reveal">
          <h2 className="i3-reportage-section-title">More on the calendar</h2>
          <div className="i3-events-more-copy">
            {content.moreEventsCopy ? (
              <I3RichHtml html={content.moreEventsCopy} />
            ) : (
              <p style={{ margin: 0 }}>
                Additional workshops and retreats will be listed here. Check back or{' '}
                <Link to="/contact">contact us</Link> to be notified.
              </p>
            )}
          </div>
        </section>

        <div className="i3-reportage-scroll" aria-hidden="true">
          <span>scroll</span>
        </div>
      </div>
      <I3MediaViewer
        isOpen={viewer.open}
        onClose={closeViewer}
        type={viewer.type}
        src={viewer.src}
        title={viewer.title}
      />
    </article>
  );
};

export default I3Events;
